import { useCallback, useContext, useRef, useMemo } from 'react';
import _ from 'underscore';
import Promise from 'bluebird';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { odata } from '@bingads-webui/http-util';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { edmCampaign } from '@bingads-webui/edm-campaign';

import {
  parsePerformanceEstimateAPI,
  parseBudgetSuggestionAPI,
  parseBudgetLandscapeAPI,
  getSuggestedBudgetAmounts,
} from './model/api-parser';
import { MODE, getParserByCampaignType, API_VERSION } from './model/constants';
import { useBudgetMetricsLogger } from './components/use-budget-metrics-logger';

// OpportunityId values returned by AdInsight can exceed Number.MAX_SAFE_INTEGER
// (e.g. 260601040000049161). jQuery's default `dataType: 'json'` handling runs the
// raw response through JSON.parse, which silently rounds such integers (turning the
// id above into 260601040000049150) before our code ever sees it. We override the
// `text json` converter for the budget-landscape call to quote the OpportunityId
// value in the raw text first, preserving the exact id as a string.
export const parseBudgetLandscapeResponse = (text) => {
  if (text === '' || text == null) {
    return text;
  }

  const safeText = text.replace(/("OpportunityId"\s*:\s*)(-?\d+)/g, '$1"$2"');
  return JSON.parse(safeText);
};

export const createOrUpdateCampaign = (data) => {
  const campaignEDM = edmCampaign.types.resolve('Model.ShoppingCampaign');
  const campaignResource = new campaignEDM.ResourceIdentifier();
  const createOrUpdateCall = data && data.Id ?
    campaignResource.bulkUpdate.bind(campaignResource) :
    campaignResource.bulkCreate.bind(campaignResource);
  return createOrUpdateCall(data);
};

export const getODataConfig = (serverContext, apiVersion) => {
  const isLocal = () => {
    const hostname = document.location.hostname.toLowerCase();
    // CCUI devdev hostnames (e.g. ccui.devdev.bingads.microsoft.com) incorrectly
    // match the ads.microsoft.com substring via bingads.microsoft.com, so check
    // devdev explicitly first — same pattern as PMaxLite signup's init-legacy-odata.
    if (hostname.indexOf('devdev') > -1) {
      return true;
    }
    return !/(^|\.)(bing)?ads(-int)?\.microsoft\.com$/.test(hostname);
  };

  let odataPath = isLocal() ? 'AdInsight/V2' : 'AdInsight/V2';

  if (apiVersion === API_VERSION.V2) {
    odataPath = isLocal() ? 'Advertiser/V2' : 'Campaign/V2';
  }

  const servicesEndpoint = apiVersion === API_VERSION.V2 ? serverContext.ServicesEndpoints.CampaignAPI : serverContext.ServicesEndpoints.AdInsightAPI;

  const oDataConfig = {
    odataToken: serverContext.Token,
    oDataToken: serverContext.Token,
    odataPath: `${servicesEndpoint}${odataPath}`,
    oDataPath: `${servicesEndpoint}${odataPath}`,
  };

  return oDataConfig;
};

export const useBudgetSuggestionRepository = ({
  mode,
  campaignId,
  campaignType,
  currency,
  campaign,
  additionalData,
  // V2 is the GA default. Consumers that need the legacy AdInsight V1 path
  // (e.g. campaigns-page BudgetSuggestionDialog for Opportunity.GetUpdatedBudgetLandscape)
  // must explicitly pass apiVersion: API_VERSION.V1.
  apiVersion = API_VERSION.V2,
  isExpansionPilotEnabled = true,
  enableInlineSuggestionTelemetry = false,
}) => {
  const serverContext = useContext(ServerContext);
  const isNotifyAdInsightEnabled = serverContext?.DynamicPermissions?.NotifyAdInsight || false;

  const { scope } = useContext(ScopeContext) || {};

  const i18n = useContext(I18nContext);

  const { aid, cid, campaignId: campaignIdFromScope } = scope.values;
  const currentCampaignId = campaignId || campaignIdFromScope;

  const { CurrentAccount: { CurrencyCode } } = serverContext;

  const {
    logBudgetFetchMetrics,
    logBudgetLandscapeMetrics,
    logCustomBudgetPerformanceMetrics,
    saveBudgetSuggestionMetrics,
    logBudgetSuggestionView,
    logBudgetSuggestionAdoption,
    logNotifyAdInsightResult,
  } = useBudgetMetricsLogger();

  const oDataConfig = useMemo(() => getODataConfig(serverContext, apiVersion), [serverContext, apiVersion]);

  const adInsightODataConfig = useMemo(() => getODataConfig(serverContext, API_VERSION.V1), [serverContext]);

  const oDataOptions = useMemo(() => ({
    contentType: 'application/json',
    path: oDataConfig.odataPath,
    oDataToken: oDataConfig.odataToken,
  }), [oDataConfig]);

  const latestSuggestedBudget = useRef([]);
  const latestOpportunityId = useRef('');
  // Retain the raw (unparsed) API responses so consumers that need fields the
  // parsers drop (e.g. the weekly performance nodes) can read them without a
  // second fetch. Purely additive — the resolved values are unchanged.
  const latestRawBudgetSuggestion = useRef(null);
  const latestRawCustomPerformance = useRef(null);

  const notifyAdInsight = useCallback(({
    id,
    budgetAmount,
    opportunityId,
  }) => {
    const url = `${adInsightODataConfig.odataPath}/Customers(${cid})/Accounts(${aid})/Opportunity.Track`;
    const payload = {
      Request: {
        '@odata.type': '#Microsoft.Advertiser.AdInsight.Opportunity.Core.Adoption.Interface.Entity.ApplyBulkOpportunitiesRequest',
        CampaignAdGroups: [{ Key: parseInt(id, 10), Value: [] }],
        Channel: 'incontextrecommendation',
        OpportunityType: 'BudgetOpportunity',
        UserAction: 'Accept',
        UserInputs: [{
          '@odata.type': '#Microsoft.Advertiser.AdInsight.Opportunity.Core.Adoption.Interface.Entity.ApplyBudgetOpportunityUserInputV2',
          NewBudget: budgetAmount,
          OpportunityId: String(opportunityId),
        }],
      },
    };

    const options = _.extend(
      {},
      oDataOptions,
      {
        url,
        data: payload,
      }
    );

    const promise = odata.post(options);

    return promise.then(() => {
      logNotifyAdInsightResult?.(true);
    }).catch((error) => {
      logNotifyAdInsightResult?.(false, error);
    });
  }, [adInsightODataConfig, aid, cid, logNotifyAdInsightResult, oDataOptions]);

  const fetchBudgetSuggestionData = useCallback((payload) => {
    let baseURL = `${oDataConfig.odataPath}/Customers(${cid})/Accounts(${aid})/BudgetSuggestion.GetBudgetSuggestion`;
    if (apiVersion === API_VERSION.V2) {
      baseURL = `${oDataConfig.odataPath}/Customers(${cid})/Accounts(${aid})/Default.BudgetSuggestion`;
    }

    const options = _.extend(
      {},
      oDataOptions,
      {
        url: baseURL,
        data: payload,
      }
    );

    const promise = odata.post(options);

    return promise.then((data) => {
      if (data) {
        latestRawBudgetSuggestion.current = data;
        const parsedBudgetSuggestion = parseBudgetSuggestionAPI(data, i18n, currency);

        logBudgetFetchMetrics({
          campaign,
          budgetResponse: parsedBudgetSuggestion,
          apiVersion,
          isExpansionPilotEnabled,
        });
        latestSuggestedBudget.current = parsedBudgetSuggestion;
        return parsedBudgetSuggestion;
      }

      return null;
    });
  }, [aid, campaign, cid, logBudgetFetchMetrics, oDataConfig.odataPath, oDataOptions, i18n, currency, apiVersion, isExpansionPilotEnabled]);

  const fetchInitialBudgetSuggestionData = useCallback(() => {
    const parser = getParserByCampaignType(campaignType);

    const payload = parser({
      additionalData, campaign, campaignType, currency: currency || CurrencyCode, apiVersion, isExpansionPilotEnabled,
    });

    return fetchBudgetSuggestionData(payload);
  }, [CurrencyCode, additionalData, campaign, campaignType, currency, fetchBudgetSuggestionData, apiVersion, isExpansionPilotEnabled]);

  const saveBudget = useCallback(({
    budgetAmount,
    budgetTier,
    biddingScheme,
  }) => {
    const modelToSave = {
      Id: campaignId,
      Budget: {
        Amount: _.isNaN(i18n.parseDecimal(budgetAmount)) ? parseFloat(budgetAmount) : i18n.parseDecimal(budgetAmount),
        Type: 'DailyStandard',
      },
      ...(
        _.isEmpty(biddingScheme) ? {} : { BiddingScheme: biddingScheme }
      ),
    };

    return createOrUpdateCampaign(modelToSave).then((res) => {
      saveBudgetSuggestionMetrics({
        campaign, budgetResponse: latestSuggestedBudget.current, appliedBudget: budgetAmount, budgetTier, apiVersion, isExpansionPilotEnabled,
      });

      if (enableInlineSuggestionTelemetry) {
        const suggestedBudget = latestSuggestedBudget.current?.Suggested?.BudgetAmountDaily || 0;
        logBudgetSuggestionAdoption({
          campaignId,
          optId: latestOpportunityId.current,
          suggestedBudget,
          savedBudget: _.isNaN(i18n.parseDecimal(budgetAmount)) ? parseFloat(budgetAmount) : i18n.parseDecimal(budgetAmount),
          currency: currency || CurrencyCode,
        });

        // Notify AdInsight (fire-and-forget). Skip when OpportunityId is empty.
        if (isNotifyAdInsightEnabled && !_.isEmpty(latestOpportunityId.current)) {
          const parsedBudgetAmount = _.isNaN(i18n.parseDecimal(budgetAmount)) ? parseFloat(budgetAmount) : i18n.parseDecimal(budgetAmount);
          notifyAdInsight({
            id: campaignId,
            budgetAmount: parsedBudgetAmount,
            opportunityId: latestOpportunityId.current,
          });
        }
      }

      return res;
    }).catch(error =>
      // eslint-disable-next-line prefer-promise-reject-errors
      Promise.reject({ error }));
  }, [
    campaignId, i18n, saveBudgetSuggestionMetrics, campaign, apiVersion, isExpansionPilotEnabled, enableInlineSuggestionTelemetry,
    isNotifyAdInsightEnabled, logBudgetSuggestionAdoption, currency, CurrencyCode, notifyAdInsight,
  ]);

  const fetchBudgetLandscapeData = useCallback(() => {
    // Opportunity.GetUpdatedBudgetLandscape is an AdInsight-only API (same family
    // as Opportunity.Track above), so it must always use adInsightODataConfig
    // regardless of the budget-suggestion apiVersion. Using the V2 (Campaign)
    // config here routes the call to Campaign/V2, which doesn't expose this API.
    const baseURL = `${adInsightODataConfig.odataPath}/Customers(${cid})/Accounts(${aid})/Campaigns(${currentCampaignId})/Opportunity.GetUpdatedBudgetLandscape`;

    const options = _.extend(
      {},
      adInsightODataConfig,
      { converters: { 'text json': parseBudgetLandscapeResponse } }
    );

    const promise = odata.get(baseURL, options);

    return promise.then((data) => {
      const parsedBudgetLandscape = parseBudgetLandscapeAPI(data, i18n, currency);

      logBudgetLandscapeMetrics({ campaign: { Id: currentCampaignId }, budgetLandscapeResponse: parsedBudgetLandscape });

      latestOpportunityId.current = parsedBudgetLandscape.OpportunityId || '';

      if (enableInlineSuggestionTelemetry) {
        logBudgetSuggestionView({
          campaignId: currentCampaignId,
          optId: parsedBudgetLandscape.OpportunityId || '',
          suggestedBudget: parsedBudgetLandscape.Suggested?.BudgetAmountDaily || 0,
          currency: currency || CurrencyCode,
        });
      }

      return parsedBudgetLandscape;
    });
  }, [adInsightODataConfig, cid, aid, currentCampaignId, i18n, currency, logBudgetLandscapeMetrics, enableInlineSuggestionTelemetry, logBudgetSuggestionView, CurrencyCode]);

  const loadBudget = useCallback(() => {
    if (mode === MODE.CREATION) {
      return fetchInitialBudgetSuggestionData();
    }

    return fetchBudgetLandscapeData();
  }, [fetchBudgetLandscapeData, fetchInitialBudgetSuggestionData, mode]);

  const fetchCustomBudgetPerformanceData = useCallback(({ budgetAmount, biddingScheme }) => {
    let baseURL = `${oDataConfig.odataPath}/Customers(${cid})/Accounts(${aid})/BudgetSuggestion.GetPerformanceEstimation`;

    if (apiVersion === API_VERSION.V2) {
      baseURL = `${oDataConfig.odataPath}/Customers(${cid})/Accounts(${aid})/Default.BudgetSuggestion`;
    }

    const updatedCampaign = {
      ...campaign,
      Budget: {
        Amount: _.isNaN(i18n.parseDecimal(budgetAmount)) ? parseFloat(budgetAmount) : i18n.parseDecimal(budgetAmount),
      },
      ...(biddingScheme && { BiddingScheme: { ...campaign.BiddingScheme, ...biddingScheme } }),
    };

    const parser = getParserByCampaignType(campaignType);

    const payload = parser({
      additionalData,
      campaign: updatedCampaign,
      currency: currency || CurrencyCode,
      latestSuggestedBudget: getSuggestedBudgetAmounts(latestSuggestedBudget.current).sort((a, b) => a - b),
      isPerformanceEstimation: true,
      apiVersion,
      isExpansionPilotEnabled,
    });

    const options = _.extend(
      {},
      oDataOptions,
      {
        url: baseURL,
        data: payload,
      }
    );

    const promise = odata.post(options);

    return promise.then((data) => {
      latestRawCustomPerformance.current = data;
      const parsedPerformanceEstimate = parsePerformanceEstimateAPI(data, i18n, currency);

      logCustomBudgetPerformanceMetrics({ performanceMetricsResponse: parsedPerformanceEstimate, apiVersion, isExpansionPilotEnabled });

      return parsedPerformanceEstimate;
    });
  }, [CurrencyCode, aid, additionalData, campaign, campaignType, cid, currency, i18n, logCustomBudgetPerformanceMetrics, oDataConfig.odataPath, oDataOptions, apiVersion, isExpansionPilotEnabled]);

  // only for online model.
  // load budget but only updating few fields in campaign object.
  const loadBudgetPartial = useCallback((args) => {
    const updatedCampaign = {
      ...campaign,
      ...args,
    };

    const parser = getParserByCampaignType(campaignType);

    const payload = parser({
      additionalData,
      campaign: updatedCampaign,
      campaignType,
      currency: currency || CurrencyCode,
      apiVersion,
      isExpansionPilotEnabled,
    });

    return fetchBudgetSuggestionData(payload);
  }, [CurrencyCode, additionalData, campaign, campaignType, currency, fetchBudgetSuggestionData, apiVersion, isExpansionPilotEnabled]);

  // Additive getters for the raw responses captured above. Stable identity so
  // consumers can list them in effect deps without re-firing.
  const getLatestRawBudgetSuggestion = useCallback(() => latestRawBudgetSuggestion.current, []);
  const getLatestRawCustomPerformance = useCallback(() => latestRawCustomPerformance.current, []);

  return {
    loadBudget,
    loadBudgetPartial,
    saveBudget,
    fetchCustomBudgetPerformanceData,
    getLatestRawBudgetSuggestion,
    getLatestRawCustomPerformance,
  };
};
